/**
 * AuthCallback — handles the `#session_id=<...>` fragment that Emergent Auth
 * redirects us to after Google sign-in. Runs BEFORE any other route to avoid
 * a race with the AuthProvider's own /auth/me check.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { exchangeSessionId, useAuth } from "../lib/auth";
import { getUserId } from "../lib/user";

export default function AuthCallback() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    // Clean the URL immediately so a reload doesn't re-process the id.
    if (window.history.replaceState) {
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
    }

    if (!sessionId) {
      nav("/", { replace: true });
      return;
    }

    exchangeSessionId(sessionId)
      .then((data) => {
        setUser(data);
        // Preserve the anonymous local user_id — it's now their canonical id too
        // (backend migrated their data if needed).
        try {
          if (data?.user_id && data.user_id !== getUserId()) {
            localStorage.setItem("boardroom_user_id", data.user_id);
          }
        } catch (err) {
          // localStorage may be blocked (Safari private mode, etc.).
          console.debug("boardroom_user_id write blocked", err);
        }
        toast.success(`Signed in as ${data.email || data.name || "you"}`);
        nav("/", { replace: true, state: { user: data } });
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Sign-in failed. Please try again.");
        nav("/", { replace: true });
      });
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#fafafa] flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <p className="label-eyebrow text-[#c89f45]">Boardroom</p>
        <p className="font-serif text-2xl mt-3">Calling the session to order…</p>
        <p className="text-sm text-[#666] mt-2">Signing you in</p>
      </div>
    </div>
  );
}
